import classNames from 'classnames'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import AddIcon from '@mui/icons-material/Add'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  Box,
  BoxProps,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'

import { getCommonState } from '@/store/common/selector'
import { setSelectedYear, setSelectedGroupPath } from '@/store/common/actions'
import { deleteKnowledgeGroup } from '@/store/knowledge/actions'
import KnowledgeService from '@/services/KnowledgeService'
import { cn } from '@/lib/utils'
import CustomMenu from '../CustomMenu'
import BasicModal from '../Dialog'
import UpdateGroupModal from './UpdateGroupModal'
import { MANAGEKNOWLEDGEGROUP } from '@/config'

const knowledgeService = new KnowledgeService()

interface KGroup {
  _id: string
  parent_id: string
  agent_id: string
  name: string
  topic?: string
  depth: number
  group_type: string
  children: KGroup[]
}

/**
 * Props for KGroupsTree, a hierarchical tree view of Knowledge Groups.
 *
 * Features and behavior:
 * - Loads top-level groups on mount and fetches children on demand when a node is expanded/clicked.
 * - Uses the Redux `selectedGroupPath` (array of ids from root to leaf) to expand the default path on mount.
 * - Accepts an optional `initialSelectedGroupId` for edit scenarios; when provided, the tree resolves and emits that selection.
 * - Emits the selected group via `onSelectGroup` both on user click and after resolving the initial selection.
 * - When `syncsState` is true (default), updates legacy Redux state for backward compatibility.
 * - Can hide system groups (group_type === 'system') with `hidesSystemGroups`.
 *
 * Usage example:
 * ```tsx
 * <KGroupsTree
 *   actions={{ add: true, edit: true, delete: true }}
 *   syncsState={false}
 *   onSelectGroup={(group) => setSelectedGroupId(group._id)}
 * />
 * ```
 */
interface KGroupsTreeProps {
  /**
   * Slot props for root container (MUI `Box`), useful to tweak padding/margins without altering component internals.
   */
  slotProps?: { root?: BoxProps }
  /**
   * Optional route: if provided, a menu action to view archived groups will be shown.
   */
  archivedRoute?: string
  /**
   * Optional route: if provided, a menu action to manage groups will be shown.
   */
  showsManageGroups?: boolean
  /**
   * Toggle visibility of action items (add/edit/delete) in the item context menu.
   */
  actions?: {
    add?: boolean
    edit?: boolean
    delete?: boolean
  }
  /**
   * Callback invoked when a group is selected, either by user click or after resolving the initial selection.
   */
  onSelectGroup?: (group: KGroup) => void
  /**
   * If true (default), selection changes are synced to Redux for backward compatibility
   * (dispatches selectedYear and selectedGroupPath). If false, the component is "local-only"
   * and will only call `onSelectGroup`.
   * @default true
   */
  syncsState?: boolean
  /**
   * Initial selected group id (useful for edit flows). When absent, the tree will use the
   * last id from Redux `selectedGroupPath` (if present) for initial selection/expansion.
   */
  initialSelectedGroupId?: string | null
  /**
   * Hide system groups (group_type === 'system') from the tree.
   * @default false
   */
  hidesSystemGroups?: boolean
  /**
   * Optional title for the tree header. Defaults to the translated "Groups" label when not provided.
   */
  title?: string
}

const KGroupListItem: React.FC<{
  group: KGroup
  // Pass pathIds to onClick so parent can compute expanded ancestors
  onClick: (group: KGroup, pathIds: string[]) => void
  actions?: {
    add?: boolean
    edit?: boolean
    delete?: boolean
  }
  getGroups?: (params?: { parentId?: string }) => void
  expandGroupIds?: string[]
  // New: path of ancestor IDs for this item; root passes []
  parentIds?: string[]
  selectedGroupId?: string | null
}> = ({
  group,
  onClick,
  actions,
  getGroups,
  expandGroupIds,
  parentIds = [],
  selectedGroupId,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [openMenu, setOpenMenu] = useState<string | null>()
  const [showsUpdateModal, setShowsUpdateModal] = useState(false)
  const [moduleEditMode, setModuleEditMode] = useState(false)
  const [deletedModuleId, setDeletedModuleId] = useState([])
  const [deleteModuleModalShow, setDeleteModuleModalShow] = useState(false)
  const isSelected = selectedGroupId === group._id

  // Compute and store full path IDs (ancestors + self)
  const pathIds = useMemo(
    () => [...parentIds, group._id],
    [parentIds, group._id],
  )
  const pathIdsRef = useRef<string[]>(pathIds)
  useEffect(() => {
    pathIdsRef.current = pathIds
  }, [pathIds])

  const updateModalHandler = () => {
    setShowsUpdateModal(!showsUpdateModal)
  }

  const confirmRenameHandler = async () => {
    getGroups()
    updateModalHandler()
  }

  const deleteModuleModalHandler = () => {
    setDeleteModuleModalShow(!deleteModuleModalShow)
  }
  const confirmDeleteModuleHandler = async (deleteId: string[]) => {
    try {
      await dispatch(deleteKnowledgeGroup(deleteId as any))
      getGroups()
      deleteModuleModalHandler()
    } catch (error: any) {
      console.log('error', error)
    }
  }

  const updateParentHandler = (isEdit: boolean = false) => {
    setModuleEditMode(isEdit)
    updateModalHandler()
  }

  return (
    <React.Fragment>
      <Tooltip title={group.name} key={group._id} placement="right-start">
        <li
          onClick={() => onClick(group, pathIds)}
          style={{ paddingLeft: `${group.depth * 8}px` }}
          // Store the path in a data attribute for future use
          data-path-ids={pathIds.join(',')}
          className={classNames(
            'tw-flex tw-items-center tw-justify-between tw-group tw-pr-3',
            {
              'clr-grey-txt': !isSelected && group.depth !== 1,
              'tw-text-white tw-bg-primary': isSelected,
              'hover:tw-bg-[#D3E8FF] hover:tw-text-[#07135F]': !isSelected,
            },
          )}
        >
          <Typography
            noWrap
            component="p"
            sx={{
              fontSize: '14px',
              lineHeight: '20px',
              fontWeight: 600,
              p: '12px',
            }}
          >
            {group.name}
          </Typography>
          <span
            className={cn(
              openMenu !== group._id && 'tw-hidden',
              'li-action-icon group-hover:tw-inline-flex',
            )}
          >
            {(actions?.add || actions?.edit || actions?.delete) && (
              <CustomMenu
                isIcon
                iconTrigger={<MoreVertIcon fontSize="small" />}
                onOpen={() => setOpenMenu(group._id)}
                onClose={() => setOpenMenu(null)}
                options={[
                  {
                    label: t('Edit'),
                    onClick: () => {
                      updateParentHandler(true)
                    },
                    hide: !actions?.edit,
                  },
                  {
                    label: t('Delete'),
                    onClick: () => {
                      setDeleteModuleModalShow(true)
                      setDeletedModuleId([group._id])
                    },
                    hide: !actions?.delete,
                  },
                  {
                    label: t('Add Subgroup'),
                    onClick: () => {
                      updateParentHandler(false)
                    },
                    hide: !actions?.add,
                  },
                ]}
              />
            )}
          </span>
        </li>
      </Tooltip>
      {expandGroupIds.includes(group._id) &&
        group.children?.map(child => (
          <KGroupListItem
            key={child._id}
            group={child}
            onClick={onClick}
            actions={actions}
            getGroups={getGroups}
            expandGroupIds={expandGroupIds}
            parentIds={pathIds}
            selectedGroupId={selectedGroupId}
          />
        ))}
      {showsUpdateModal && (
        <UpdateGroupModal
          show={showsUpdateModal}
          onHide={() => setShowsUpdateModal(false)}
          _id={moduleEditMode ? group._id : null}
          name={group.name}
          topic={group.topic}
          parent_id={moduleEditMode ? null : group._id}
          editMode={moduleEditMode}
          confirmHandler={confirmRenameHandler}
        />
      )}
      <BasicModal
        open={deleteModuleModalShow}
        onClose={deleteModuleModalHandler}
        showCloseButton
        sx={{ p: 2, width: 430, height: 'auto' }}
      >
        <Typography
          variant="body1"
          sx={{ my: 3, fontWeight: 600 }}
          component="p"
        >
          {t(
            'Knowledge Group will be permanently deleted. All knowledge items/Quizzes in the knowledge group will be shifted to uncategorised',
          )}
        </Typography>
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button variant="outlined" onClick={deleteModuleModalHandler}>
            {t('No, Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => confirmDeleteModuleHandler(deletedModuleId)}
          >
            {t('Yes, Delete')}
          </Button>
        </Stack>
      </BasicModal>
    </React.Fragment>
  )
}

/**
 * KGroupsTree
 *
 * A hierarchical tree component that displays knowledge groups and lets users select, add, edit, or delete groups.
 *
 * Data loading strategy:
 * - On mount, fetches top-level groups. If a `selectedGroupPath` exists in Redux, it expands and fetches
 *   all ancestors in that path. If `initialSelectedGroupId` is provided, it resolves and emits that selection.
 * - On item click, ensures the group's children are loaded (fetch-on-click if needed), expands the full path
 *   of ancestors, and calls `onSelectGroup`.
 *
 * Backward compatibility:
 * - When `syncsState` is true, the component will dispatch updates to legacy Redux slices (selectedYear,
 *   selectedGroupPath) so existing screens keep working, while consumers can migrate to `selectedGroupPath` as
 *   the single source of truth.
 */
const KGroupsTree: React.FC<KGroupsTreeProps> = ({
  slotProps,
  archivedRoute,
  showsManageGroups = false,
  actions,
  onSelectGroup,
  syncsState = true,
  initialSelectedGroupId,
  hidesSystemGroups = false,
  title,
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const commonState = useSelector(getCommonState)
  const dispatch = useDispatch()
  // Resolve initial selected group id from props or fallback to the last id in selectedGroupPath
  const resolvedInitialSelectedGroupId = React.useMemo(() => {
    if (initialSelectedGroupId) return initialSelectedGroupId
    const path = commonState.selectedGroupPath
    return path && path.length > 0 ? path[path.length - 1] : null
  }, [initialSelectedGroupId, commonState.selectedGroupPath])
  const [groups, setGroups] = useState<KGroup[]>([])
  const [expandGroupIds, setExpandGroupIds] = useState<string[]>(
    commonState.selectedGroupPath && commonState.selectedGroupPath.length > 0
      ? commonState.selectedGroupPath
      : resolvedInitialSelectedGroupId
        ? [resolvedInitialSelectedGroupId]
        : [],
  )
  const [showAddGroupModal, setShowAddGroupModal] = useState(false)
  const [selectedKGroupId, setSelectedKGroupId] = useState<string | null>(
    resolvedInitialSelectedGroupId,
  )

  // Re-introduce back-compat sync helpers
  const syncSelectedYear = (year?: KGroup) => {
    if (syncsState) {
      dispatch(setSelectedYear(year as any))
    }
  }

  const syncSelectedGroupPath = (selectedGroupPath: string[]) => {
    if (syncsState) {
      dispatch(setSelectedGroupPath(selectedGroupPath))
    }
  }

  const refreshGroups = async (params?: { parentId?: string }) => {
    try {
      const res: KGroup[] = (await knowledgeService.getKnowledgeGroups({
        parentId: params?.parentId,
      })) as any

      // Helper to filter out system groups if the flag is enabled
      const filterGroups = (arr: KGroup[]) =>
        hidesSystemGroups ? arr.filter(g => g.group_type !== 'system') : arr

      if (!params?.parentId) {
        const topLevelGroups = filterGroups(res)
        setGroups(topLevelGroups)
        // Use selectedGroupPath for default expansion
        const selectedPath =
          commonState.selectedGroupPath &&
          commonState.selectedGroupPath.length > 0
            ? commonState.selectedGroupPath
            : []

        if (selectedPath.length > 0) {
          setExpandGroupIds(selectedPath)
          // Back-compat: sync selectedGroupId and selectedYear
          const lastId = selectedPath[selectedPath.length - 1]
          const topLevelYear = topLevelGroups.find(
            g => g._id === selectedPath[0],
          )
          setSelectedKGroupId(lastId)
          if (topLevelYear) syncSelectedYear(topLevelYear)

          // Recursively fetch children for each group in the path
          let currentLevelGroups = topLevelGroups
          selectedPath.forEach(groupId => {
            const group = currentLevelGroups.find(g => g._id === groupId)
            if (group) {
              refreshGroups({ parentId: group._id })
              currentLevelGroups = group.children || []
            }
          })
        } else if (topLevelGroups[0]?._id) {
          // Fallback: expand and select the first top-level group when there's no prior state
          const firstGroup = topLevelGroups[0]
          refreshGroups({ parentId: firstGroup._id })
          setExpandGroupIds([firstGroup._id])
          setSelectedKGroupId(firstGroup._id)
          // Sync legacy state and the new selectedGroupPath for consistency
          syncSelectedYear(firstGroup)
          syncSelectedGroupPath([firstGroup._id])
          // Inform parent about the default selection
          onSelectGroup?.(firstGroup)
        }
      } else {
        // Recursively update children for the matching parent anywhere in the tree
        const applyChildren = (
          nodes: KGroup[],
          parentId: string,
          children: KGroup[],
        ): KGroup[] =>
          nodes.map(n => {
            if (n._id === parentId) {
              return { ...n, children }
            }
            return {
              ...n,
              children: n.children
                ? applyChildren(n.children, parentId, children)
                : n.children,
            }
          })

        const children = filterGroups(res)
        setGroups(prev => applyChildren(prev, params.parentId!, children))
      }
    } catch (error: any) {
      console.error('Error fetching groups:', error)
    }
  }

  useEffect(() => {
    refreshGroups()

    const selectedPath = commonState.selectedGroupPath
    const resolvedSelectedId =
      initialSelectedGroupId ??
      (syncsState && selectedPath?.length
        ? selectedPath[selectedPath.length - 1]
        : null)

    if (resolvedSelectedId) {
      ;(async () => {
        try {
          const res =
            await knowledgeService.getKnowledgeGroup(resolvedSelectedId)
          const group =
            res?.data?.data?.group ||
            res?.data?.data ||
            res?.data?.group ||
            res?.data
          if (group) {
            setSelectedKGroupId(resolvedSelectedId)
            // If we have a path, keep it expanded
            if (selectedPath?.length) {
              setExpandGroupIds(selectedPath)
            }
            onSelectGroup?.(group)
          }
        } catch (err) {
          console.error('Failed to fetch selected group', err)
        }
      })()
    }
  }, [])

  const handleClickGroup = (group: KGroup, pathIds: string[]) => {
    setSelectedKGroupId(group._id)
    // Sync all stores for back-compat
    syncSelectedGroupPath(pathIds)

    // Ensure children are loaded before or while expanding
    if (!group.children || group.children.length === 0) {
      refreshGroups({ parentId: group._id })
    }

    // Determine top-level year from current groups list
    const topLevelYear = groups.find(g => g._id === pathIds[0])

    if (group.parent_id) {
      // Subgroup selected
      if (topLevelYear) syncSelectedYear(topLevelYear)
    } else {
      // Top-level selected
      syncSelectedYear(group)
    }

    if (!expandGroupIds.includes(group._id)) {
      setExpandGroupIds(pathIds)
    }
    onSelectGroup?.(group)
  }

  return (
    <Box {...slotProps?.root}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        py="8px"
        px="12px"
        sx={{
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          backgroundColor: '#eee',
        }}
      >
        <h2 className="mb-0 font-1">{title ?? t('Groups')}</h2>
        {(actions?.add || archivedRoute || showsManageGroups) && (
          <Stack direction="row" spacing={1}>
            {actions?.add && (
              <IconButton
                onClick={() => {
                  setShowAddGroupModal(true)
                }}
                size="small"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            )}
            {(archivedRoute || showsManageGroups) && (
              <CustomMenu
                isIcon
                iconTrigger={<MoreVertIcon fontSize="small" />}
                slotProps={{ trigger: { sx: { color: '#676267' } } }}
                options={[
                  archivedRoute
                    ? {
                        label: t('View Archived'),
                        onClick: () => {
                          history.push(archivedRoute)
                        },
                      }
                    : null,
                  showsManageGroups
                    ? {
                        label: t('Manage Groups'),
                        onClick: () => {
                          history.push(MANAGEKNOWLEDGEGROUP)
                        },
                      }
                    : null,
                ]}
              />
            )}
          </Stack>
        )}
      </Stack>
      <div>
        <ul>
          {groups.map((year: any) => (
            <KGroupListItem
              key={year._id}
              group={year}
              onClick={(group, pathIds) => {
                handleClickGroup(group, pathIds)
              }}
              actions={actions}
              getGroups={refreshGroups}
              expandGroupIds={expandGroupIds}
              parentIds={[]}
              selectedGroupId={selectedKGroupId}
            />
          ))}
        </ul>
      </div>
      {(actions?.add || actions?.edit) && (
        <UpdateGroupModal
          show={showAddGroupModal}
          onHide={() => setShowAddGroupModal(false)}
          _id={null}
          name={''}
          topic={''}
          parent_id={null}
          editMode={false}
          confirmHandler={() => {
            setShowAddGroupModal(false)
            refreshGroups()
          }}
        />
      )}
    </Box>
  )
}

export default KGroupsTree
